import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, GraduationCap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const navItems = [
  { key: 'nav.home', href: '#home' },
  { key: 'nav.courses', href: '#courses' },
  { key: 'nav.msCitAi', href: '#ai' },
  { key: 'nav.abacus', href: '#abacus' },
  { key: 'nav.about', href: '#about' },
  { key: 'nav.gallery', href: '#gallery' },
  { key: 'nav.contact', href: '#contact' },
];

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'mr' : 'en');
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cp-orange to-cp-orange-light flex items-center justify-center group-hover:scale-110 transition-transform">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="text-lg font-bold text-white leading-tight">CAREER POINT</span>
              <span className="text-xs text-cp-orange block -mt-1">COMPUTER EDUCATION</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                className="text-sm font-medium text-gray-300 hover:text-cp-orange transition-colors relative group"
              >
                {t(item.key)}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cp-orange transition-all group-hover:w-full" />
              </a>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-600 text-sm text-gray-300 hover:border-cp-orange hover:text-cp-orange transition-colors"
            >
              <span className={language === 'en' ? 'text-cp-orange font-medium' : ''}>EN</span>
              <span className="text-gray-500">|</span>
              <span className={language === 'mr' ? 'text-cp-orange font-medium' : ''}>मराठी</span>
            </button>

            {/* Admission Button */}
            <a
              href="#contact"
              className="hidden md:flex btn-primary px-5 py-2 rounded-full text-sm font-semibold text-white"
            >
              {t('nav.admissionOpen')}
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-white hover:text-cp-orange transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass border-t border-gray-700"
          >
            <div className="container-custom py-4">
              <div className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-3 text-gray-300 hover:text-cp-orange hover:bg-white/5 rounded-lg transition-colors"
                  >
                    {t(item.key)}
                  </a>
                ))}
                
                {/* Mobile Language Toggle */}
                <button
                  onClick={() => {
                    toggleLanguage();
                    setIsOpen(false);
                  }}
                  className="flex sm:hidden items-center justify-center gap-2 px-4 py-3 text-gray-300 hover:text-cp-orange hover:bg-white/5 rounded-lg transition-colors"
                >
                  <span className={language === 'en' ? 'text-cp-orange font-medium' : ''}>
                    {t('lang.english')}
                  </span>
                  <span className="text-gray-500">|</span>
                  <span className={language === 'mr' ? 'text-cp-orange font-medium' : ''}>
                    {t('lang.marathi')}
                  </span>
                </button>

                <a
                  href="#contact"
                  onClick={() => setIsOpen(false)}
                  className="mt-2 btn-primary px-4 py-3 rounded-lg text-center font-semibold text-white"
                >
                  {t('nav.admissionOpen')}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}